import { debounce } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useProfile, useToasts } from "../../../contexts";
import { useAxios } from "../../../hooks";
import { FormData, defaultFormData } from "../../../models";
import { UseFormReturn, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Schema } from "zod";
import { useIdleTimer } from "react-idle-timer";
import { Endpoints } from "../../../api";

interface UseAutoSaveFormProps {
    formDataId?: string | number;
    schema?: Schema;
    formType?: "form" | "donor" | "tissue";
    isReadonlyModal?: boolean;
    fetchedFormData?: FormData;
}

export interface IUseAutoSaveForm {
    methods: UseFormReturn<any, unknown, any>;
    isDirty: boolean;
    isSaving: boolean;
    isLocked: boolean;
    isLockedByMe: boolean;
    isSigned: boolean;
    isReadonly: boolean;
    lockedByName: string;
    formLoaded: boolean;
    formData: FormData | undefined;
    refetchFormData: () => Promise<void>;
    saveFormData: (values?: any) => Promise<void>;
}

export function useAutoSaveForm({
    formDataId,
    schema,
    formType: formTypeArg,
    isReadonlyModal,
    fetchedFormData,
}: Readonly<UseAutoSaveFormProps>) {
    const { currentUser } = useProfile();
    const { get, put } = useAxios();
    const { errorToast } = useToasts();
    const [formData, setFormData] = useState<FormData | undefined>(undefined);
    const [formLoaded, setFormLoaded] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const myName = `${currentUser?.firstName} ${currentUser?.lastName}`;
    const isLocked = useMemo(
        () => !!formData?.lockedByUserId && formData.lockedByUserId !== currentUser?.id,
        [currentUser?.id, formData]
    );
    const isLockedByMe = !!formData?.lockedByUserId && formData.lockedByUserId === currentUser?.id;
    const isSigned = !!formData?.signatures?.filter((s) => !s.scope)?.length;
    const isReadonly = isLocked || (isSigned && !isLockedByMe) || (isReadonlyModal ?? false);
    const formType = formTypeArg ?? "form";

    const baseUrl = useMemo(() => {
        if (formType === "donor") {
            return Endpoints.Donors;
        }
        if (formType === "tissue") {
            return Endpoints.Tissues;
        }
        return Endpoints.FormData;
    }, [formType]);

    const lockedByName = useMemo(
        () =>
            !!formData?.lockedByUserId && formData.lockedByUserId !== currentUser?.id
                ? (formData.lockedByName ?? "")
                : "",
        [currentUser?.id, formData?.lockedByName, formData?.lockedByUserId]
    );

    const unlockForm = useCallback(async () => {
        // execute when leaving form to leave available for other users
        if (!formDataId || isReadonlyModal) return;
        try {
            await put<FormData>({
                url: `${baseUrl}/unlock/${formDataId}`,
            });
        } catch (e: unknown) {
            errorToast(e);
        }
    }, [baseUrl, formDataId, put, errorToast, isReadonlyModal]);

    useEffect(() => {
        return () => {
            unlockForm();
        };
    }, [unlockForm]);

    const methods = useForm({
        resolver: schema ? zodResolver(schema) : undefined,
        resetOptions: {
            keepDefaultValues: true,
        },
    });

    const {
        formState: { isDirty, isSubmitSuccessful },
        reset,
    } = methods;

    useEffect(() => {
        if (isSubmitSuccessful && formData && !isReadonlyModal) {
            reset(formData.data, { keepDirtyValues: false });
        }
    }, [formData, isLocked, isSubmitSuccessful, reset, isReadonlyModal]);

    const fetchFormData = useCallback(async () => {
        if (!formDataId) return;
        if (isReadonlyModal && fetchedFormData) {
            setFormData(fetchedFormData);
            reset(fetchedFormData.data);
            setFormLoaded(true);
        } else {
            try {
                const response = await get<FormData>({ url: `${baseUrl}/${formDataId}` });
                setFormData(response);
                reset(response.data);
                setFormLoaded(true);
            } catch (e: unknown) {
                errorToast(e);
            }
        }
    }, [formDataId, isReadonlyModal, fetchedFormData, reset, get, baseUrl, errorToast]);

    const refetchFormData = useCallback(async () => {
        setFormLoaded(false);
        await fetchFormData();
    }, [fetchFormData]);

    const handleSave = useCallback(
        async (values: any) => {
            try {
                const request: FormData = {
                    ...defaultFormData,
                    ...formData,
                    data: {
                        ...formData?.data,
                        ...values,
                    },
                    lockedByUserId: currentUser?.id,
                    lockedByName: myName,
                    lockedOn: new Date().toISOString(),
                    form: undefined,
                };
                const response = await put<FormData>({ url: baseUrl, data: request });

                if (response.lockedByUserId !== currentUser?.id) {
                    errorToast(`Form is locked by ${response.lockedByName}.`);
                }

                setFormData(response);
            } catch (e: unknown) {
                errorToast(e);
            }
        },
        [baseUrl, currentUser?.id, errorToast, formData, myName, put]
    );

    const handleAutoSave = useCallback(
        async (values?: any) => {
            if (!formData || (!isDirty && !values)) return;
            setIsSaving(true);
            if (values) {
                await methods.handleSubmit(() => handleSave(values))();
            } else {
                await methods.handleSubmit(handleSave)();
            }
            setIsSaving(false);
        },
        [formData, handleSave, isDirty, methods]
    );

    const handleAutoSaveRef = useRef<any>(null);
    handleAutoSaveRef.current = handleAutoSave;

    const debouncedSave = useMemo(() => {
        return debounce(() => handleAutoSaveRef.current(), 2000);
    }, []);

    useIdleTimer({
        onAction: debouncedSave,
        events: ["keydown", "mousedown", "touchstart"],
        element: document,
    });

    useEffect(() => {
        fetchFormData();
    }, [fetchFormData]);

    useEffect(() => {
        const handleTabKey = (event: KeyboardEvent) => {
            if (event.key === "Tab" && !isSaving && isDirty) {
                handleAutoSave();
            }
        };
        document.addEventListener("keydown", handleTabKey);
        return () => document.removeEventListener("keydown", handleTabKey);
    }, [handleAutoSave, isDirty, isSaving]);

    return useMemo<IUseAutoSaveForm>(
        () => ({
            methods,
            isDirty,
            isSaving,
            isLocked,
            isLockedByMe,
            isReadonly,
            isSigned,
            lockedByName,
            formLoaded,
            formData,
            refetchFormData,
            saveFormData: handleAutoSave,
        }),
        [
            formData,
            formLoaded,
            handleAutoSave,
            isDirty,
            isLocked,
            isLockedByMe,
            isReadonly,
            isSaving,
            isSigned,
            lockedByName,
            methods,
            refetchFormData,
        ]
    );
}
